import { APIOrganizationUser, ArdoqId } from '@ardoq/api-types';
import type { ComponentTypeForLegend } from '@ardoq/view-legend';
import {
  LoadedGraphWithViewpointMode,
  OtherLabel,
  UrlFieldValue,
  ViewError,
} from '@ardoq/graph';
import {
  ContextSort,
  EnhancedScopeData,
  FormattingAndLabelRules,
  RepresentationData,
} from '@ardoq/data-model';
import type { FilterColorData } from '@ardoq/scope-data';
import type { PermissionContext } from '@ardoq/access-control';
import {
  ComponentMatrixGroup,
  ComponentMatrixMeasurements,
  ComponentMatrixViewModel,
  ComponentMatrixViewSettingsState,
  HeaderCellModel,
} from '../types';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import {
  SelectedReferenceTypeInfo,
  type TraversedReferenceTypeInfo,
} from '@ardoq/settings-bar';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import type { ErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import { SelectOption } from '@ardoq/select';
import type { ExtractPayload } from '@ardoq/rxbeach';
import type { ViewContext } from '@ardoq/view-settings';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import type { componentMatrixToggleCollapseGroup } from '../actions';

export type GroupColorInfo = {
  fillColor: string;
  borderColor?: string;
  /** true if fillColor is a dark color. dark colors have relative luminance less than 0.179 and thus should have a white foreground color for text and icons. */
  isDark: boolean;
};

export type ComponentMatrix2Group = ComponentMatrixGroup & GroupColorInfo;

export type ComponentMatrix2HeaderCellModel = HeaderCellModel &
  GroupColorInfo & {
    /** Number of wrapped lines for the rendered label, given the actual
     * rowHeadersAreaWidth. Set during measurement for both component row
     * headers (in `toViewpointRowModels`) and list-value row headers (in
     * `augmentListValueCellLabelLineCount`); feeds the wrap-aware stripe
     * height. */
    labelLineCount?: number;
    /** True when the cell's row is at its minimum height (the header label is
     * the binding constraint), false when body content or a user resize made
     * the row taller. Drives the label's cross-axis alignment: centered within
     * the min-height band, top-aligned once the row grows past it so the label
     * stays visible (ARD-32762). Computed in `measure`; undefined for
     * directly-built story cells (treated as at-min → center). */
    isMinHeight?: boolean;
    /** Wrap-aware stripe height for this cell's label, in px. Used by
     * componentMatrixRowHeaderCell to size the colored stripe at the top of
     * an expanded group's card so wrapped labels don't get clipped to the
     * constant ROW_HEADER_STRIPE_HEIGHT. Optional because stories build
     * header cells directly without going through the augmentation step in
     * toViewpointRowModels; the renderer floors at ROW_HEADER_STRIPE_HEIGHT
     * so undefined behaves like the legacy constant. */
    stripeHeight?: number;
    /** Icon / image data for cells whose group is a referenced component.
     * Undefined for list-value cells. */
    representationData?: RepresentationData;
    /** Pre-computed wrapped text lines for each `otherLabels` badge, computed
     * from the same `wrapString` pass that drives the row height. Passing these
     * to `OtherLabel`'s `wrappedLines` prop replaces CSS line-breaking with
     * JS line-breaking, so the rendered badge height always matches the
     * measurement. Undefined when there are no `otherLabels`. */
    otherLabelWrappedLines?: string[][];
  };

export type GroupWithGridInfo = ComponentMatrix2Group & {
  gridColumn: number;
  colSpan: number;
  /** Number of wrapped lines for the list-value column header label.
   * Undefined for component columns (which render their own header card). */
  labelLineCount?: number;
  /** True when this column header is at its level's minimum height (its label
   * is as tall as the tallest in the level), false when a taller sibling
   * stretched the level. Drives list-value column label alignment: centered at
   * min, top-aligned when the level is taller (ARD-32762). */
  isMinHeight?: boolean;
  /** Icon / image data for groups whose owner type is a referenced
   * component. Undefined for list-value columns. */
  representationData?: RepresentationData;
  /** Pre-computed wrapped text lines for each `otherLabels` badge, mirrors
   * the same field on `ComponentMatrix2HeaderCellModel` for row headers.
   * Undefined when there are no `otherLabels`. */
  otherLabelWrappedLines?: string[][];
};

export type PersistedExpandedState = {
  expandedRowAddresses: (string | null)[][];
  expandedColumnAddresses: (string | null)[][];
};

/**
 * User-adjusted column widths keyed by the column's leaf address joined by `|`.
 * Per ARD-31416 the "even width" cap and `MAX_COLUMN_HEADER_WIDTH` are
 * discarded for columns the user has manually resized; values are still
 * clamped to the measured minimum so labels never truncate below content.
 */
export type UserAdjustedColumnWidths = Record<string, number>;

/**
 * User-adjusted row heights keyed by the row's leaf address joined by `|`.
 * Symmetric to {@link UserAdjustedColumnWidths} per ARD-31416. Values are
 * clamped to the measured minimum so wrapped labels and badges never get
 * clipped; above the minimum the user value is used verbatim (no upper cap).
 */
export type UserAdjustedRowHeights = Record<string, number>;

export enum AxisSortOrder {
  DEFAULT = 'default',
  ALPHA_AZ = 'alpha-az',
  ALPHA_ZA = 'alpha-za',
  COUNT_HIGH_LOW = 'count-high-low',
  COUNT_LOW_HIGH = 'count-low-high',
}

export type ComponentMatrix2ViewSettings = {
  selectedFieldNamesX: string[];
  selectedFieldNamesY: string[];
  referenceTypeX: SelectedReferenceTypeInfo | null;
  referenceTypeY: SelectedReferenceTypeInfo | null;
  showEmptyValues: boolean;
  isLegendActive: boolean;
  /** ARD-31884 component-type selector. Empty array means "All" — the
   * matrix renders every component type available in the loaded graph
   * (the default for first-paint and existing viewpoints with no value
   * persisted yet). When non-empty, the loaded scope component ids are
   * filtered to only components whose `.type` is in the list. */
  selectedComponentTypes: string[];
  persistedExpandedState: PersistedExpandedState;
  sortOrderX?: AxisSortOrder;
  sortOrderY?: AxisSortOrder;
  displayTextInViewX?: string | null;
  displayTextInViewY?: string | null;
  userAdjustedColumnWidths: UserAdjustedColumnWidths;
  userAdjustedRowHeights: UserAdjustedRowHeights;
  /** User-dragged width of the row-headers area (the leftmost column), in px.
   * Undefined until the user resizes it — then it overrides the auto content
   * width, clamped to the level-headers-area width so the axis label cards stay
   * intact (ARD-32764). Persisted; double-click the handle clears it. */
  userAdjustedRowHeadersWidth?: number;
  useParentAsX: boolean;
  useParentAsY: boolean;
  additionalHeaderFieldNamesX: string[];
  additionalHeaderFieldNamesY: string[];
  colorRowColumnHeaderByComponent: boolean;
};

/**
 * The Component Matrix arranges components in a grid. `componentTypes` selects
 * which components are plotted; the `xAxis` and `yAxis` each group those
 * components some way, and every cell holds the components that fall under its
 * x-axis group and its y-axis group.
 */
export type SettableComponentMatrix2ViewSettings = {
  /**
   * Component type names to plot in the cells. An empty array plots every
   * component type present in the data. Defaults to `[]` (all types). Take each
   * name from `viewState.componentMatrix2.availableComponentTypes`.
   */
  componentTypes: string[];
  /**
   * The x-axis (columns, horizontal). Defaults to `{ kind: 'none' }`
   * (ungrouped). Include this only when
   * `viewState.componentMatrix2.canSetAxes` is true.
   */
  xAxis: ComponentMatrix2Axis;
  /**
   * The y-axis (rows, vertical). Defaults to `{ kind: 'none' }` (ungrouped).
   * Include this only when `viewState.componentMatrix2.canSetAxes` is true.
   */
  yAxis: ComponentMatrix2Axis;
  /**
   * When `true`, each axis keeps an extra "no value" group — a column on the
   * x-axis and a row on the y-axis — for the components that have no value for
   * the field (or no reference of the type) that axis groups by. When `false`,
   * those "no value" groups are hidden. Defaults to `false`.
   */
  showEmptyValues: boolean;
  /**
   * When `true`, each row and column header is coloured by its component type's
   * colour; when `false`, all headers are grey. Defaults to `true`.
   */
  colorHeadersByComponent: boolean;
  /**
   * Whether the legend is shown — a panel keying the component types in the
   * view (with their colours) and any conditional formatting. Off by default.
   * Named as the shared legend infrastructure writes it.
   */
  isLegendActive: boolean;
};

/**
 * One axis of the matrix, discriminated by how it groups the plotted
 * components. `none` leaves the axis ungrouped (a single row or column); the
 * other kinds group the components and carry the shared display settings.
 */
export type ComponentMatrix2Axis =
  | {
      /** The axis is not grouped: a single row or column holds every component. */
      kind: 'none';
    }
  | ({
      /** Group this axis by the values of a list field. */
      kind: 'field';
      /**
       * Name of the list field whose values group this axis. Take it from
       * `viewState.componentMatrix2.axisFieldNames`.
       */
      fieldName: string;
    } & ComponentMatrix2AxisDisplaySettings)
  | ({
      /**
       * Group this axis by a component type reached from each plotted component
       * by following a reference.
       */
      kind: 'reference';
      /**
       * The reference to follow. Must match a reference type that is currently
       * traversable from the data: take one entry from
       * `viewState.componentMatrix2.axisReferences` and copy it as it is given.
       */
      reference: SelectedReferenceTypeInfo;
    } & ComponentMatrix2AxisDisplaySettings)
  | ({
      /** Group this axis by each component's parent in the component hierarchy. */
      kind: 'parent';
    } & ComponentMatrix2AxisDisplaySettings);

/**
 * Display settings shared by every grouped axis (all kinds except `none`). Omit
 * the ones the request does not change: each is left as the axis already has it.
 */
export type ComponentMatrix2AxisDisplaySettings = {
  /** How the groups on this axis are ordered. Omit to keep the current order. */
  sortOrder?: AxisSortOrder;
  /**
   * A custom label for this axis's header. `null` uses the automatic label (the
   * field name, the reference name, or "Hierarchy" for a parent axis). Omit to
   * keep the current label.
   */
  displayText?: string | null;
  /**
   * Names of extra fields whose values are shown as badges inside this axis's
   * header cards. Take them from
   * `viewState.componentMatrix2.axisHeaderFieldNamesX` for the x-axis and from
   * `viewState.componentMatrix2.axisHeaderFieldNamesY` for the y-axis. Those
   * lists describe the axes as they are configured now, so do not change this
   * axis and set its badges in the same call: change the axis, set the badges
   * next. Omit to keep the current badges.
   */
  additionalHeaderFieldNames?: string[];
};

export type ViewpointModeComponentMatrixMeasurements =
  ComponentMatrixMeasurements & {
    columnHeadersAreaHeight: number;
    columnHeadersAreaGridTemplateRows: string;
    columnHeaderLevelStripeHeights: number[];
    /** Stable per-leaf-column keys aligned with `columnHeaderAndColumnWidths`,
     * used by the resize handle to identify which column was dragged
     * (ARD-31416). Derived from each leaf group's address path. */
    leafColumnKeys: string[];
    /** Measured minimum widths per leaf column (header width / cell content
     * width, whichever is larger), used by the resize handle to clamp the
     * drag so the user cannot shrink a column below its content width. */
    leafColumnMinWidths: number[];
    /** Stable per-row keys aligned with `rowHeights`, used by the row resize
     * handle to identify which row was dragged (ARD-31416). Derived from each
     * row's leaf header cell address. */
    leafRowKeys: string[];
    /** Measured minimum heights per row (cell content height / row header
     * height, whichever is larger), used by the row resize handle to clamp
     * the drag so the user cannot shrink a row below its content height. */
    leafRowMinHeights: number[];
    /** Minimum width of the row-headers area (the level-headers-area width), used
     * by the row-headers resize handle to clamp the drag so the axis label cards
     * stay intact (ARD-32764). The user may shrink below the row-header content
     * width (labels truncate) but not below this. */
    rowHeadersAreaMinWidth: number;
  };

export type ColumnResizeHandleModel = {
  columnKey: string;
  gridColumn: number;
  gridRowEnd: number;
  currentWidth: number;
  minWidth: number;
};

export type BodyCellItemModel = {
  componentId: ArdoqId;
  fillColor: string;
  hoverFillColor: string;
  isDark: boolean;
  isContext: boolean;
  componentLabel: string;
  representationData: RepresentationData;
  urlFieldValues: UrlFieldValue[] | null;
  otherLabels: OtherLabel[];
  /** Pre-computed wrapped text lines per badge, from the same width the cell
   * height is measured at. When set, each badge renders these exact lines via
   * `OtherLabel`'s `wrappedLines` prop so the rendered height always matches the
   * measurement and never clips (ARD-33210). */
  otherLabelWrappedLines?: string[][];
};

export type BodyCellModel = {
  items: BodyCellItemModel[];
  columnIndex: number;
  columnSpan: number;
  rowSpan: number;
};

export type RowElementModel = {
  headerCells: ComponentMatrix2HeaderCellModel[];
  bodyCells: BodyCellModel[];
  /** Extra px encoded at the bottom of this row's grid track to create a visual
   * gap between root groups in the row-header area. 0 for all rows except the
   * last leaf row of every non-final root group. */
  rowBottomGap?: number;
};

export type LevelHeaderViewModel = {
  fieldName: string;
  referenceDirection?: 'outgoing' | 'incoming';
};

export type ComponentMatrix2ItemAddress = {
  rowValues: (string | null)[];
  columnValues: (string | null)[];
};

export type ComponentMatrix2Item = {
  address: ComponentMatrix2ItemAddress;
  componentId: ArdoqId;
  /** Reference ids for the references this cell item represents — the reference
   * to its row header and/or column header, in that order, for axes with a
   * reference type selected. Resolved to reference "other labels" rendered
   * beneath the component's own labels (ARD-32765). */
  referenceIds?: ArdoqId[];
};

export type ViewpointModeComponentMatrixViewModel = {
  columns: Map<string | null, ComponentMatrixGroup>;
  rows: Map<string | null, ComponentMatrixGroup>;
  items: ComponentMatrix2Item[];
  columnsByDepth: ComponentMatrix2Group[][];
  columnsByDepthWithGridInfo: GroupWithGridInfo[][];
  rowsByDepth: ComponentMatrixGroup[][];
  columnLevelLabels: string[];
  rowLevelLabels: string[];
  rowLevelHeader?: LevelHeaderViewModel;
  columnLevelHeader?: LevelHeaderViewModel;
  measurements: ViewpointModeComponentMatrixMeasurements;
  rowModels: RowElementModel[];
  hoveredComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  /** User-adjusted column widths from viewSettings, threaded through so the
   * measure step can override the default per-column width formula. Per
   * ARD-31416 these widths are clamped to the measured min but bypass the
   * `MAX_COLUMN_HEADER_WIDTH` cap. */
  userAdjustedColumnWidths: UserAdjustedColumnWidths;
  /** User-adjusted row heights from viewSettings, threaded through so the
   * measure step can override the content-driven row height. Symmetric to
   * `userAdjustedColumnWidths` per ARD-31416. */
  userAdjustedRowHeights: UserAdjustedRowHeights;
  /** User-dragged width of the row-headers area, threaded from viewSettings so
   * the measure step can override the auto left-column width (ARD-32764). */
  userAdjustedRowHeadersWidth?: number;
  /** Per-leaf-column resize-handle props, ready to render. Built once at
   * view-model assembly so the body view stays a pure mapper. */
  resizeHandles: ColumnResizeHandleModel[];
  legendComponentTypes: ComponentTypeForLegend[];
};

export type ComponentTypeStyle = {
  color: string;
  representationData: RepresentationData;
};

export type ComponentMatrix2ViewSettingsState =
  ComponentMatrixViewSettingsState & {
    listFields: FieldWithPermissions[];
    additionalHeaderFieldsX: FieldWithPermissions[];
    additionalHeaderFieldsY: FieldWithPermissions[];
    availableComponentTypes: string[];
    componentTypeStylesByName: Record<string, ComponentTypeStyle>;
  };

export type ComponentMatrix2SettingsBounds = {
  availableComponentTypes: string[];
  axisFieldNames: string[];
  axisHeaderFieldNamesX: string[];
  axisHeaderFieldNamesY: string[];
  axisReferences: TraversedReferenceTypeInfo[];
  /**
   * Counts list fields the reader may not be allowed to see, so it can be true
   * while `axisFieldNames` is empty.
   */
  canSetAxes: boolean;
};

export type ComponentMatrix2AxisSources = {
  fieldNamesX: string[];
  fieldNamesY: string[];
  referenceX: TraversedReferenceTypeInfo | null;
  referenceY: TraversedReferenceTypeInfo | null;
  headerFieldNamesX: string[];
  headerFieldNamesY: string[];
};

export const COMPONENT_MATRIX_2_LAYOUT_SETTING_KEYS = [
  'persistedExpandedState',
  'userAdjustedColumnWidths',
  'userAdjustedRowHeights',
  'userAdjustedRowHeadersWidth',
] as const satisfies readonly (keyof ComponentMatrix2ViewSettings)[];

export type ComponentMatrix2StoredSettableSettings = Omit<
  ComponentMatrix2ViewSettings,
  (typeof COMPONENT_MATRIX_2_LAYOUT_SETTING_KEYS)[number]
>;

export type ComponentMatrix2AiViewProperties = {
  settingsBounds: ComponentMatrix2SettingsBounds;
  settableViewSettings: ComponentMatrix2StoredSettableSettings;
};

export type ComponentMatrix2SettingsControlsDependencies =
  ComponentMatrix2AiViewProperties & {
    viewSettings: ComponentMatrix2ViewSettings;
    viewSettingsState: ComponentMatrix2ViewSettingsState;
    userLocale: Locale;
  };

export type ComponentMatrix2Config = {
  isPresentationMode: boolean;
  hideRowHeadersResizeHandle?: boolean;
};

export type ViewpointModeComponentMatrixViewModelProperties = {
  viewSettingsState: ComponentMatrix2ViewSettingsState;
  viewSettings: ComponentMatrix2ViewSettings;
  settingsBounds: ComponentMatrix2SettingsBounds;
  settableViewSettings: ComponentMatrix2StoredSettableSettings;
  viewModel: ViewpointModeComponentMatrixViewModel;
  hasReferences: boolean;
  hasFilteredNullItems: boolean;
  errors: ViewError[];
  contextComponentId: ArdoqId | null;
  i18nLocale: I18nLocale;
  userLocale: Locale;
  errorInfoBoxLabels: ErrorInfoBoxLabels;
  config: ComponentMatrix2Config;
};

type SharedComponentMatrix2Commands = {
  onComponentClick: (componentId: ArdoqId) => void;
  onComponentDoubleClick: (componentId: ArdoqId) => void;
  onUrlFieldValueIconClick: (
    urlFieldValues: UrlFieldValue[],
    element: HTMLElement
  ) => void;
  updateViewContext: (viewContext: ViewContext) => void;
  updateViewProperties: (
    viewProperties: ViewpointModeComponentMatrixViewModelProperties
  ) => void;
  updateViewLegendSettings: (isActive: boolean) => void;
};

export type ComponentMatrix2ResizeCommands = {
  onColumnResize: (
    currentWidths: UserAdjustedColumnWidths,
    columnKey: string,
    width: number
  ) => void;
  onColumnResizeHandleDoubleClick: (
    currentWidths: UserAdjustedColumnWidths,
    columnKey: string
  ) => void;
  onRowResize: (
    currentHeights: UserAdjustedRowHeights,
    rowKey: string,
    height: number
  ) => void;
  onRowResizeHandleDoubleClick: (
    currentHeights: UserAdjustedRowHeights,
    rowKey: string
  ) => void;
  onRowHeadersResize: (width: number) => void;
  onRowHeadersResizeHandleDoubleClick: VoidFunction;
};

export type ComponentMatrix2RenderingCommands = {
  onItemMouseEnter: (itemId: ArdoqId | null) => void;
  onItemMouseLeave: VoidFunction;
  onBackgroundClick: VoidFunction;
  onItemSelect: (itemId: ArdoqId) => void;
  onGroupExpanderClick: (
    payload: ExtractPayload<typeof componentMatrixToggleCollapseGroup>
  ) => void;
  onCollapseAllRowsClick: VoidFunction;
  onExpandAllRowsClick: VoidFunction;
  onCollapseAllColumnsClick: VoidFunction;
  onExpandAllColumnsClick: VoidFunction;
};

export type ComponentMatrix2Commands = SharedComponentMatrix2Commands &
  ComponentMatrix2ResizeCommands &
  ComponentMatrix2RenderingCommands;

export type ComponentMatrix2DataLayerCommands =
  SharedComponentMatrix2Commands & {
    onHoveredComponentChange: (componentId: ArdoqId | null) => void;
    onFocusedComponentChange: (componentId: ArdoqId | null) => void;
    updateViewSettings: (
      settings: Partial<ComponentMatrix2ViewSettings>
    ) => void;
    updateViewComponentIds: (componentIds: ArdoqId[]) => void;
  };

export type ViewpointModeComponentMatrixViewProperties =
  ViewpointModeComponentMatrixViewModelProperties & {
    commands: ComponentMatrix2Commands;
    viewStatusReporter: ViewStatusReporter;
    /** Token of the data work the view finishes once it has rendered it; `null` when nothing is tracked. */
    dataWorkToken: string | null;
  };

export type ComponentMatrix2BuiltState = {
  viewSettingsState: ComponentMatrix2ViewSettingsState;
  viewSettings: ComponentMatrix2ViewSettings;
  settingsBounds: ComponentMatrix2SettingsBounds;
  settableViewSettings: ComponentMatrix2StoredSettableSettings;
  hasReferences: boolean;
  hasFilteredNullItems: boolean;
  errors: ViewError[];
  contextComponentId: ArdoqId | null;
  i18nLocale: I18nLocale;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  errorInfoBoxLabels: ErrorInfoBoxLabels;
  sort: ContextSort;
  userLocale: Locale;
  /** Stream snapshot used by the augmentation pipeline to resolve
   * `representationData` and other per-component view data as pure
   * scope-data reads. Stored on state so reducers like
   * `toggleCollapseGroup` (which re-run `augmentViewModel`) can reuse the
   * scope data the last `reset()` saw. */
  scopeData: EnhancedScopeData;
  /** The loaded graph the last `reset()` saw. Its reference identity is the
   * dataset-identity signal for carrying the bypass-limit decision across
   * redraws (stable) while resetting it on a real re-traversal, and it lets
   * `handleBypassLimit` re-run `reset()` without an imperative `loadedGraph$`
   * read. */
  loadedGraph: LoadedGraphWithViewpointMode;
  /** Current user's permission view; used by `getFieldsForViewpoint`
   * to annotate field options with `isConfidential` so the dropdown
   * can render the redacted state for fields the user can't see. */
  permissionContext: PermissionContext;
  /** Conditional-formatting colors derived from the formatting rules, used by
   * the augmentation pass to resolve fill colors via the pure
   * `componentUtils.getComponentDisplayColorAsSVGAttributes`. */
  filterColorData: FilterColorData;
  bypassLimit: boolean;
  formattingAndLabelRules: FormattingAndLabelRules['rules'];
};

/** The matrix built from a loaded graph, before the saved expanded state and sizes are applied. */
export type ComponentMatrix2SourceData = ComponentMatrix2BuiltState & {
  rawViewModel: ComponentMatrixViewModel;
};

export type ViewpointModeComponentMatrixViewStreamState =
  ComponentMatrix2BuiltState & {
    viewModel: ViewpointModeComponentMatrixViewModel;
  };

export type NestedCountMaps = Map<string, Map<string | null, number>>;

export enum Axis {
  ROW = 'row',
  COLUMN = 'column',
}

export enum AxisSourceType {
  FIELD = 'field',
  REF = 'ref',
  PARENT = 'parent',
}

export enum AxisSourceGroupKind {
  PARENT_RELATIONSHIP = 'parent-relationship',
  RELATED_COMPONENTS = 'related-components',
  FIELDS = 'fields',
}

export type CountSortContext = {
  axis: Axis;
  depth: number;
  parentAddress: (string | null)[];
  nestedCountMaps: NestedCountMaps;
};

export type AxisSourceValue =
  | { type: AxisSourceType.REF; ref: SelectedReferenceTypeInfo }
  | { type: AxisSourceType.FIELD; fieldName: string }
  | { type: AxisSourceType.PARENT };

/** SelectOption extended with an optional pre-rendered label node.
 * When set, DropdownMenuOption renders it instead of the plain string label,
 * allowing rich JSX (e.g. renderReferencedComponentLabel) while keeping
 * the string label for react-select's built-in text filtering. */
export type AxisSourceOption = SelectOption<AxisSourceValue> & {
  labelNode?: React.ReactNode;
};
